import PropTypes from 'prop-types'

import React, {useEffect, useState} from 'react'

import {Stack, Typography} from '@mui/material'

import Button from '../../../../shared_components/mui_base_components/button/Button'
import SummariesTable from './summaries_table/SummariesTable'
import SelectFilter from './SelectFilter'
import CheckboxFilter from './CheckboxFilter'
import TablePagination from './TablePagination'

import {formatDate} from '../../../../lib/dateTools'
import {EVERPLANS_FUNERAL_HOME} from '../../lib/firmTools'

const parseStatusForDisplay = statusString => {
  if (!statusString) return []

  const statuses = statusString.split(',').map(status => status.trim())

  return statuses.map(status => ({text: status, className: `${status.toLowerCase().replace(' ', '-')}`}))
}

import './funeralHomeClientSummariesTable.scss'

const FuneralHomeClientSummariesTable = ({
  checkboxFilterOptions, clientSummaries, isFiltering, ...otherProps
}) => {
  const defaultColumns = [
    {
      Header: 'Name',
      accessor: 'name',
      id: 'name',
      minWidth: 160,
      filterable: true
    },
    {
      id: 'status',
      Header: 'Status',
      accessor: 'status',
      minWidth: 110,
      Cell: ({value}) => <StatusCell value={value} />,
      filterable: true,
      Filter: columnProps => (
        <SelectFilter
          {...columnProps}
          options={[
            {name: 'Active', id: 'Active'},
            {name: 'Pending', id: 'Pending'},
            {name: 'Draft', id: 'Draft'},
            {name: 'Declined', id: 'Declined'},
            {name: 'Deceased', id: 'Deceased'}
          ]}
        />
      )
    },
    {
      id: 'created-at',
      Header: 'Date Added (UTC)',
      accessor: client => formatDate(client['created-at']),
      minWidth: 110
    },
    {
      id: 'last-login-date',
      Header: 'Last Login',
      accessor: client => formatDate(client['last-login-date']),
      minWidth: 110
    },
    {
      id: 'afp-name',
      Header: 'Advance Planner',
      accessor: 'afp-name',
      minWidth: 130,
      filterable: true,
      Filter: columnProps => (
        <CheckboxFilter
          {...columnProps}
          options={checkboxFilterOptions.afpNames || []}
          column={columnProps.column}
        />
      )
    }
  ]

  const [columns, setColumns] = useState(defaultColumns)

  const funeralHomeColumn = {
    id: 'firm-name',
    Header: 'Funeral Home',
    accessor: 'firm-name',
    minWidth: 150,
    filterable: true,
    Filter: columnProps => (
      <CheckboxFilter
        {...columnProps}
        options={checkboxFilterOptions.firmNames}
        column={columnProps.column}
      />
    )
  }

  const membershipColumn = {
    id: 'limited-client',
    Header: 'Membership',
    accessor: client => (client['limited-client'] === false ? 'Premium' : 'Free'),
    minWidth: 110,
    Cell: ({value}) => <MembershipCell value={value} />,
    filterable: true,
    Filter: columnProps => (
      <SelectFilter
        {...columnProps}
        options={[
          {name: 'Premium', id: 'false'},
          {name: 'Free', id: 'true'}
        ]}
        column={columnProps.column}
      />
    )
  }

  useEffect(() => {
    const updatedColumns = [...defaultColumns]

    // Add Funeral Home column if multiple funeral homes exist
    if (checkboxFilterOptions.firmNames?.length > 1)
      updatedColumns.splice(4, 0, funeralHomeColumn)

    // Add Membership column at the end if user is a quarterback
    if (otherProps.isQuarterback)
      updatedColumns.push(membershipColumn)

    setColumns(updatedColumns)
  }, [checkboxFilterOptions.firmNames, otherProps.isQuarterback])

  const noClientsMessage = () => {
    if (otherProps.loading) return null

    if (isFiltering) {
      return (
        <div className='no-clients-message'><div>No matching results</div></div>
      )
    } else {
      return (
        <div className='no-clients-message welcome-message'>
          <h1>Welcome to {EVERPLANS_FUNERAL_HOME}!</h1>
          <div>
            The names of families will appear here once they are added to Everplans.
          </div>
        </div>
      )
    }
  }

  return (
    <div className={`funeral-home-client-summaries-table ${otherProps.isQuarterback ? 'quarterback-view' : ''}`}>
      <Stack
        className='heading-container'
        direction='row'
        justifyContent='space-between'
        alignItems='center'>
        <Typography
          className='heading'
          data-testid='heading'
          variant='h6'>
          Families
        </Typography>
        {
          otherProps.isQuarterback && (
            <Button
              className='add-client-button'
              onClick={otherProps.onAddClient}>
              Add
            </Button>
          )
        }
      </Stack>
      <SummariesTable
        {...otherProps}
        data={clientSummaries}
        dataTestid='funeral-home-client-summaries-table'
        defaultSorted={[{id: 'created-at', desc: true}]}
        columns={columns}
        minRows={6}
        NoDataComponent={noClientsMessage}
        PaginationComponent={() => (
          <TablePagination
            currentPageIndex={otherProps.page}
            numberOfRowsPerPage={otherProps.pageSize}
            onPageChange={otherProps.onPageChange}
            pageCount={otherProps.pages}
            setNumberOfRowsPerPage={otherProps.onPageSizeChange}
          />
        )}
      />
    </div>
  )
}

FuneralHomeClientSummariesTable.propTypes = {
  checkboxFilterOptions: PropTypes.shape({
    afpNames: PropTypes.array,
    firmNames: PropTypes.array
  }),
  clientSummaries: PropTypes.array,
  isFiltering: PropTypes.bool,
  isQuarterback: PropTypes.bool,
  onAddClient: PropTypes.func,
  value: PropTypes.string
}

const StatusCell = ({value}) => {
  if (!value) return null
  const statusParts = parseStatusForDisplay(value)

  return (
    <span>
      {statusParts.map((status, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span>, </span>}
          <span className={status.className}>{status.text}</span>
        </React.Fragment>
      ))}
    </span>
  )
}

StatusCell.propTypes = {
  value: PropTypes.string
}

const MembershipCell = ({value}) => {
  if (!value) return null

  return (
    <span className={`membership ${value.toLowerCase()}`}>
      {value}
    </span>
  )
}

MembershipCell.propTypes = {
  value: PropTypes.string
}

export default FuneralHomeClientSummariesTable
